import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const PaymentInstructions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { product, quantity } = location?.state || {};

  const [formData, setFormData] = useState({
    fullName: '',
    fullAddress: '',
    phoneNumber: '',
    productName: product?.name || ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData?.fullAddress?.trim()) {
      newErrors.fullAddress = 'Full address is required';
    }
    
    if (!formData?.phoneNumber?.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^[0-9]{10,}$/?.test(formData?.phoneNumber?.replace(/[^0-9]/g, ''))) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (!formData?.productName?.trim()) {
      newErrors.productName = 'Product name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleProceedToPayment = () => {
    if (validateForm()) {
      // Store form data in sessionStorage for reference
      sessionStorage.setItem('orderInfo', JSON.stringify(formData));
      
      // Redirect to PayPal
      window.open('https://paypal.me/INFUHG', '_blank');
      
      // Navigate to confirmation page
      navigate('/');
    }
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-[var(--color-background)] flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} color="var(--color-error)" />
          <h2 className="text-2xl font-bold text-foreground mt-4">No Product Selected</h2>
          <p className="text-muted-foreground mt-2">Please select a product to continue.</p>
          <Button onClick={() => navigate('/')} className="mt-6">
            Return to Homepage
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Payment Instructions - INF LABS</title>
        <meta name="description" content="Complete your order information before payment" />
      </Helmet>

      <div className="min-h-screen bg-[var(--color-background)]">
        <Header />
        
        <main className="pt-[100px] pb-12 px-4 md:px-6 lg:px-8">
          <div className="max-w-[800px] mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">
                Complete Your Order Information
              </h1>
              <p className="text-lg text-muted-foreground">
                Please provide the following information before proceeding to payment
              </p>
            </div>

            {/* Product Summary */}
            <div className="mb-8 p-6 rounded-xl bg-card border border-border">
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Package" size={24} color="var(--color-primary)" />
                Order Summary
              </h2>
              <div className="flex items-center gap-4">
                <img 
                  src={product?.image} 
                  alt={product?.imageAlt || product?.name}
                  className="w-20 h-20 object-cover rounded-lg"
                />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground">{product?.name}</h3>
                  <p className="text-muted-foreground">Quantity: {quantity || 1}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">
                    ${((product?.price || 0) * (quantity || 1))?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 md:p-8 rounded-xl bg-card border border-border mb-8">
              <h2 className="text-xl font-bold text-foreground mb-6 flex items-center gap-2">
                <Icon name="FileText" size={24} color="var(--color-primary)" />
                Required Information
              </h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    name="fullName"
                    value={formData?.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    className={errors?.fullName ? 'border-error' : ''}
                  />
                  {errors?.fullName && (
                    <p className="text-error text-sm mt-1">{errors?.fullName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Full Address *
                  </label>
                  <textarea
                    name="fullAddress"
                    value={formData?.fullAddress}
                    onChange={handleInputChange}
                    placeholder="Enter your complete shipping address"
                    rows={3}
                    className={`w-full px-4 py-3 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all duration-300 ${errors?.fullAddress ? 'border-error' : ''}`}
                  />
                  {errors?.fullAddress && (
                    <p className="text-error text-sm mt-1">{errors?.fullAddress}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Phone Number *
                  </label>
                  <Input
                    type="tel"
                    name="phoneNumber"
                    value={formData?.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    className={errors?.phoneNumber ? 'border-error' : ''}
                  />
                  {errors?.phoneNumber && (
                    <p className="text-error text-sm mt-1">{errors?.phoneNumber}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Product You Bought *
                  </label>
                  <Input
                    type="text"
                    name="productName"
                    value={formData?.productName}
                    onChange={handleInputChange}
                    placeholder="Product name"
                    readOnly
                    className="bg-muted/50"
                  />
                </div>
              </div>
            </div>

            {/* Shipping Notice */}
            <div className="p-6 rounded-xl bg-accent/10 border border-accent/30 mb-8">
              <div className="flex items-start gap-4">
                <Icon name="Truck" size={32} color="var(--color-accent)" className="flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-accent mb-2">
                    Next Day Shipping Guarantee
                  </h3>
                  <p className="text-foreground">
                    Your order will be shipped the next business day after payment confirmation.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Instructions */}
            <div className="p-6 rounded-xl bg-primary/10 border border-primary/30 mb-8">
              <div className="flex items-start gap-4">
                <Icon name="MessageSquare" size={32} color="var(--color-primary)" className="flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-primary mb-2">
                    After Payment
                  </h3>
                  <p className="text-foreground mb-2">
                    Once you complete your payment through PayPal, please text the following to:
                  </p>
                  <p className="text-xl font-bold text-primary mb-2">
                    801-810-4671
                  </p>
                  <ul className="list-disc list-inside text-foreground space-y-1">
                    <li>Your full name</li>
                    <li>Your full address</li>
                    <li>Your phone number</li>
                    <li>Product you purchased</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                size="lg"
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate(-1)}
              >
                Go Back
              </Button>
              <Button
                variant="default"
                size="lg"
                fullWidth
                iconName="CreditCard"
                iconPosition="left"
                onClick={handleProceedToPayment}
              >
                Proceed to PayPal Payment
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PaymentInstructions;